/**
 * The cockpit's search schemas (PRD-625): each screen's URL parameters, parsed once, so a deep link is a
 * screen the reviewer can send. A refused parameter names its field. The book vocabulary is keyed by the
 * GENERATED `BookName`: a book the contract gains fails `tsc` here until it is listed, so no hand-typed list
 * can drift from the server's.
 */
import type { BookName } from '../api';

/** A search parameter that could not be read; names the field. */
export class SearchRefused extends Error {
  readonly field: string;

  constructor(field: string, reason: string) {
    super(`${field}: ${reason}`);
    this.name = 'SearchRefused';
    this.field = field;
  }
}

const BOOKS_KEYED = {
  'NEC 2020': null,
  'IMC 2021': null,
  'AR PC 2018': null,
  'AR FGC 2018': null,
  'IPC 2021': null,
  'IES RP-1-21': null,
  'IESNA Lighting Handbook 9th': null,
  'NECA 1-2015': null,
  'TEST BOOK': null,
} satisfies Record<BookName, null>;

const BOOKS: ReadonlySet<string> = new Set(Object.keys(BOOKS_KEYED));

/** Every book of the generated vocabulary, in the order the contract lists them. */
export const BOOK_NAMES: readonly BookName[] = Object.keys(BOOKS_KEYED).filter(isBook);

/** Whether a value is a book of the generated vocabulary. */
export function isBook(value: unknown): value is BookName {
  return typeof value === 'string' && BOOKS.has(value);
}

/** One field of the raw search, or undefined. */
function fieldOf(raw: Record<string, unknown>, field: string): unknown {
  return new Map(Object.entries(raw)).get(field);
}

function bookOf(raw: Record<string, unknown>, field: string): BookName {
  const value = fieldOf(raw, field);
  if (!isBook(value)) throw new SearchRefused(field, 'not a book the cockpit knows');
  return value;
}

/** Which books a screen is scoped to: every book, or the one the reviewer came from. */
export type BookScope = { readonly kind: 'all-books' } | { readonly kind: 'book'; readonly book: BookName };

/** Which articles a screen is scoped to: every article, or the one the reviewer came from. */
export type ArticleScope =
  | { readonly kind: 'all-articles' }
  | { readonly kind: 'article'; readonly article: string };

/**
 * THE EDGE for the optional `code` parameter: a URL cannot carry a variant, so its absence is read HERE, into
 * a `BookScope`, and nowhere else; every reader matches the variant exhaustively.
 */
function bookScopeOf(raw: Record<string, unknown>, field: string): BookScope {
  return fieldOf(raw, field) === undefined ? { kind: 'all-books' } : { kind: 'book', book: bookOf(raw, field) };
}

/** The URL turns a bare number into a number: a name or an article reads back as the text it was. */
function textOf(raw: Record<string, unknown>, field: string): string {
  const value = fieldOf(raw, field);
  if (typeof value === 'string' && value !== '') return value;
  if (typeof value === 'number') return String(value);
  throw new SearchRefused(field, 'required text');
}

/** THE EDGE for the optional `article` parameter, as `bookScopeOf` is for `code`: absence is read here only. */
function articleScopeOf(raw: Record<string, unknown>, field: string): ArticleScope {
  return fieldOf(raw, field) === undefined
    ? { kind: 'all-articles' }
    : { kind: 'article', article: textOf(raw, field) };
}

/** A category's name as a URL carries it: lowercase words joined by dashes (the server's KindName). */
const KIND_WORD = /^[a-z]+$/;

/** Whether a text is a category's name: one or more lowercase words, each joined to the next by one dash. */
function isKindName(text: string): boolean {
  return text.split('-').every((word) => KIND_WORD.test(word));
}

/** The walk on a sheet: the book and the 0-based sheet, with no leaf named (a sheet with no leaves to act on). */
export interface WalkSheetSearch {
  readonly book: BookName;
  readonly page: number;
  readonly stand: 'sheet';
}

/** The walk on a leaf: the sheet, the leaf's row, and the category the walk moves along (a verdict moves it). */
export interface WalkLeafSearch {
  readonly book: BookName;
  readonly page: number;
  readonly stand: 'leaf';
  readonly element: number;
  readonly kind: string;
}

/** The walk's search: a variant by `stand`, so a sheet names no leaf and a leaf always names its category. */
export type WalkSearch = WalkSheetSearch | WalkLeafSearch;

/**
 * The walk: a book and the 0-based sheet, and the stand it takes. The stand is read at this edge: absent, the walk
 * stands on the sheet; `leaf` names the leaf's row and its category, both required together.
 */
export function parseWalkSearch(raw: Record<string, unknown>): WalkSearch {
  const page = fieldOf(raw, 'page');
  if (typeof page !== 'number' || !Number.isInteger(page) || page < 0) {
    throw new SearchRefused('page', 'not a whole number from 0');
  }
  const book = bookOf(raw, 'book');
  const stand = fieldOf(raw, 'stand');
  if (stand !== undefined && stand !== 'sheet' && stand !== 'leaf') {
    throw new SearchRefused('stand', 'neither sheet nor leaf');
  }
  if (stand !== 'leaf') {
    if (fieldOf(raw, 'element') !== undefined) throw new SearchRefused('element', 'names a leaf but the stand is not leaf');
    if (fieldOf(raw, 'kind') !== undefined) throw new SearchRefused('kind', 'names a category but the stand is not leaf');
    return { book, page, stand: 'sheet' };
  }
  const element = fieldOf(raw, 'element');
  if (typeof element !== 'number' || !Number.isInteger(element) || element < 0) {
    throw new SearchRefused('element', 'not a leaf row');
  }
  const kind = fieldOf(raw, 'kind');
  if (typeof kind !== 'string' || !isKindName(kind)) {
    throw new SearchRefused('kind', 'not a category name');
  }
  return { book, page, stand: 'leaf', element, kind };
}

/** Which rule the screen stands on: the queue's next pending rule (the search names none), or one the link names. */
export type RulePick =
  | { readonly kind: 'queue' }
  | { readonly kind: 'named'; readonly name: string; readonly jurisdiction: string };

export interface RuleSearch {
  readonly pick: RulePick;
  readonly books: BookScope;
  readonly articles: ArticleScope;
}

/**
 * The rule: with no `name` the queue's next pending rule under the book and article; with a `name` its
 * `jurisdiction` too. The book rides as `code` (the twin's spelling) or `book` (the cockpit's own).
 */
export function parseRuleSearch(raw: Record<string, unknown>): RuleSearch {
  const field = fieldOf(raw, 'code') === undefined ? 'book' : 'code';
  const pick: RulePick =
    fieldOf(raw, 'name') === undefined
      ? { kind: 'queue' }
      : { kind: 'named', name: textOf(raw, 'name'), jurisdiction: textOf(raw, 'jurisdiction') };
  return { pick, books: bookScopeOf(raw, field), articles: articleScopeOf(raw, 'article') };
}

/** The held tray's search: the book the reviewer came from, or none (the tray lists every book's entries). */
export type HeldSearch = { readonly book: BookName } | { readonly book?: undefined };

/** The held tray: optionally the book it was reached from (absence is read here, at the edge). */
export function parseHeldSearch(raw: Record<string, unknown>): HeldSearch {
  return fieldOf(raw, 'book') === undefined ? {} : { book: bookOf(raw, 'book') };
}

export interface ArticleSearch {
  readonly books: BookScope;
  readonly articles: ArticleScope;
}

/** Article done: optionally the book and article. */
export function parseArticleSearch(raw: Record<string, unknown>): ArticleSearch {
  return { books: bookScopeOf(raw, 'code'), articles: articleScopeOf(raw, 'article') };
}

/** The picker's search: the book whose categories it lists, or none, and the books are listed to choose from. */
export type KindsSearch = { readonly book: BookName } | { readonly book?: undefined };

/** The picker: optionally the book (absence is read here, at the edge, and by the screen's one test of `book`). */
export function parseKindsSearch(raw: { readonly book?: unknown }): KindsSearch {
  return raw.book === undefined ? {} : { book: bookOf(raw, 'book') };
}
